import { CONFIG } from '../config.js';
import { playNoiseBurst, playTone } from './proceduralSynth.js';
import { Random } from '../utils/Random.js';

/**
 * Procedural footsteps — walk vs sprint timing comes from FootstepController events.
 * Optional buffers: assets.footstepWalk / footstepSprint in CONFIG.audio.assets.
 */
export class FootstepAudio {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {{
   *   config: import('../config.js').CONFIG['audio']['footsteps'];
   *   getBuffer: (key: string) => AudioBuffer | null;
   * }} options
   */
  constructor(ctx, bus, { config, getBuffer }) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config;
    this._getBuffer = getBuffer;
    this._rng = new Random(config.seed);
  }

  /**
   * Spatial NPC step (quieter, distance-attenuated).
   * @param {import('../player/FootstepController.js').FootstepEvent} event
   * @param {{ distance: number; pan: number; onRoad?: boolean }} spatial
   */
  onNpcStep(event, spatial) {
    const cfg = CONFIG.audio.npcFootsteps;
    const maxDist = cfg.maxDistance;
    const t = 1 - Math.min(1, spatial.distance / maxDist);
    if (t <= 0.02) return;

    const roadScale = spatial.onRoad ? (cfg.roadGainScale ?? 1) : 1;
    const falloff = t * (0.4 + 0.6 * t);
    let gain =
      this._gainFor({ ...event, sprinting: false }) *
      cfg.gainScale *
      roadScale *
      falloff;

    const detune = this._rng.range(-160, 160);
    let filterFreq = this._rng.range(240, 480);
    if (spatial.onRoad) {
      filterFreq *= 1.2;
      gain *= 1.05;
    }

    const panner = this._ctx.createStereoPanner();
    panner.pan.value = Math.max(
      -1,
      Math.min(1, spatial.pan * cfg.panStrength),
    );

    const env = this._ctx.createGain();
    env.gain.value = gain;
    env.connect(panner);
    panner.connect(this._bus);

    playNoiseBurst(this._ctx, env, {
      duration: 0.052,
      gain: 1,
      filterFreq,
      filterQ: 0.65,
    });

    playTone(this._ctx, env, {
      type: 'sine',
      frequency: 68 + this._rng.range(-8, 8),
      detune,
      duration: 0.065,
      gain: spatial.onRoad ? 0.62 : 0.5,
      attack: 0.002,
      decay: 0.045,
    });

    window.setTimeout(() => {
      panner.disconnect();
      env.disconnect();
    }, 120);
  }

  /** @param {import('../player/FootstepController.js').FootstepEvent} event */
  onStep(event) {
    const walkBuf = this._getBuffer('footstepWalk');
    const sprintBuf = this._getBuffer('footstepSprint');
    const buffer = event.sprinting
      ? sprintBuf ?? walkBuf
      : walkBuf;

    if (buffer) {
      this._playBuffer(buffer, event);
    } else {
      this._playProcedural(event);
    }
  }

  dispose() {
    this._rng = null;
  }

  /**
   * @param {AudioBuffer} buffer
   * @param {import('../player/FootstepController.js').FootstepEvent} event
   */
  _playBuffer(buffer, event) {
    const source = this._ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this._playbackRate(event);

    const env = this._ctx.createGain();
    env.gain.value = this._gainFor(event);

    source.connect(env);
    env.connect(this._bus);
    source.start();
  }

  /** @param {import('../player/FootstepController.js').FootstepEvent} event */
  _playProcedural(event) {
    const gain = this._gainFor(event);
    const detune = this._rng.range(-120, 120);
    const filterFreq = event.sprinting
      ? this._rng.range(420, 780)
      : this._rng.range(280, 520);

    playNoiseBurst(this._ctx, this._bus, {
      duration: event.sprinting ? 0.035 : 0.055,
      gain: gain * (event.sprinting ? 0.9 : 1),
      filterFreq,
      filterQ: event.sprinting ? 0.9 : 0.65,
    });

    playTone(this._ctx, this._bus, {
      type: 'sine',
      frequency: event.sprinting ? 95 : 72,
      detune,
      duration: event.sprinting ? 0.05 : 0.07,
      gain: gain * 0.55,
      attack: 0.002,
      decay: event.sprinting ? 0.035 : 0.05,
    });

    if (event.index % 2 === 0) {
      playTone(this._ctx, this._bus, {
        type: 'triangle',
        frequency: this._rng.range(180, 260),
        detune: detune * 0.5,
        duration: 0.03,
        gain: gain * 0.08,
        attack: 0.001,
        decay: 0.025,
      });
    }
  }

  /** @param {import('../player/FootstepController.js').FootstepEvent} event */
  _gainFor(event) {
    const base = event.sprinting
      ? this._config.sprintGain
      : this._config.walkGain;
    const wobble = this._rng.range(0.85, 1.15);
    const speedScale = Math.min(1.2, 0.7 + event.speed * 0.06);
    return base * wobble * speedScale;
  }

  /** @param {import('../player/FootstepController.js').FootstepEvent} event */
  _playbackRate(event) {
    const base = event.sprinting ? 1.12 : 1;
    return base * this._rng.range(0.94, 1.06);
  }
}
